import React from 'react';

export const BlogClientExtension = (props) => {
    return (
        <div className='blog-con'>
            <hr />
            <article>
                <section>
                    <h2>What Are Client Extensions?</h2>
                    <p><strong>Client Extensions </strong>are a modern, decoupled way to extend and customize Liferay without touching the core codebase or deploying OSGi modules. Unlike traditional plugins, they allow developers to inject CSS, JavaScript, custom elements, and even full applications in a clean, modular way—often hosted externally.</p>
                </section>
                <section>
                    <h2>Advantages of Client Extension</h2>
                    <ul>
                        <li><strong>No need for redeploys: </strong>Changes can be made and reflected instantly without restarting the server.</li>
                        <li><strong>Decoupled and maintainable: </strong>Reduces dependency on Liferay’s internal APIs, minimizing upgrade headaches.</li>
                        <li><strong>Cloud-native ready: </strong>Perfect for Liferay's cloud environments and headless architectures.</li>
                        <li><strong>Multiple types: </strong>From custom CSS and JS to widgets, remote apps, theme styles, and global JS injections.</li>
                    </ul>
                </section>
                <section>
                    <h2>Types of Client Extensions</h2>
                    <table border="1" cellspacing="0" cellpadding="8">
                        <thead>
                            <tr>
                            <th><strong>Category</strong></th>
                            <th><strong>Extension Name</strong></th>
                            <th><strong>Key (JSON <code>type</code>)</strong></th>
                            <th><strong>Description / Use Case</strong></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td rowspan="12"><strong>UI & Frontend</strong></td>
                                <td>Custom Element</td>
                                <td><code>customElement</code></td>
                                <td>Embed custom UI components (e.g., React/Vue) as Web Components into Liferay pages.</td>
                            </tr>
                            <tr>
                                <td>IFrame App</td>
                                <td><code>iframe</code></td>
                                <td>Embed external applications or tools via iframe.</td>
                            </tr>
                            <tr>
                                <td>Global JavaScript</td>
                                <td><code>globalJS</code></td>
                                <td>Inject JavaScript globally, e.g., for analytics or UI customization.</td>
                            </tr>
                            <tr>
                                <td>Global CSS</td>
                                <td><code>globalCSS</code></td>
                                <td>Inject CSS across all pages (site-wide styling).</td>
                            </tr>
                            <tr>
                                <td>Theme CSS</td>
                                <td><code>themeCSS</code></td>
                                <td>Extend or override the theme's built-in styles.</td>
                            </tr>
                            <tr>
                                <td>Theme Favicon</td>
                                <td><code>themeFavicon</code></td>
                                <td>Customize the favicon shown in the browser tab.</td>
                            </tr>
                            <tr>
                                <td>Theme Spritemap</td>
                                <td><code>themeSpritemap</code></td>
                                <td>Replace Liferay’s default icon set with a custom spritemap.</td>
                            </tr>
                            <tr>
                                <td>Static HTML Content</td>
                                <td><code>staticContent</code></td>
                                <td>Serve predefined HTML or static content blocks in the UI.</td>
                            </tr>
                            <tr>
                                <td>Language Keys / Translations</td>
                                <td><code>i18n</code></td>
                                <td>Add or override localized strings for multilingual support.</td>
                            </tr>
                            <tr>
                                <td>Frontend Data Set Filter</td>
                                <td><code>frontendDataSetFilter</code></td>
                                <td>Add custom filters to frontend data tables or lists.</td>
                            </tr>
                            <tr>
                                <td>Frontend Data Set Renderer / View</td>
                                <td><code>frontendDataSetView</code>, <code>cellRenderer</code></td>
                                <td>Customize how frontend datasets render rows or fields.</td>
                            </tr>
                            <tr>
                                <td>Editor Config Contributor</td>
                                <td><code>editorConfigContributor</code></td>
                                <td>Modify WYSIWYG editor settings (e.g., toolbar, plugins).</td>
                            </tr>
                            <tr>
                                <td rowspan="7"><strong>Configuration & Platform</strong></td>
                                <td>Instance Settings Configuration</td>
                                <td><code>instanceSettings</code></td>
                                <td>Define or override portal-wide instance settings (e.g., default language, company name).</td>
                            </tr>
                            <tr>
                                <td>System Settings Contributor</td>
                                <td><code>systemSettingsContributor</code></td>
                                <td>Customize OSGi-level configuration settings.</td>
                            </tr>
                            <tr>
                                <td>OAuth2 App for Headless Integration</td>
                                <td><code>oauth2ApplicationHeadlessServer</code></td>
                                <td>Register an OAuth2 application for headless API authentication.</td>
                            </tr>
                            <tr>
                                <td>OAuth2 App for Web or SPA Clients</td>
                                <td><code>oauth2ApplicationUserAgent</code></td>
                                <td>Register OAuth2 for browser apps using authorization code flow.</td>
                            </tr>
                            <tr>
                                <td>Settings Icon / Menu Item</td>
                                <td><code>settingsIcon</code></td>
                                <td>Add entries to the Liferay Control Panel or site menus.</td>
                            </tr>
                            <tr>
                                <td>Panel App Contributor</td>
                                <td><code>panelApp</code></td>
                                <td>Create custom Control Panel applications or site administration entries.</td>
                            </tr>
                            <tr>
                                <td>Blueprints (Search Tuning)</td>
                                <td><code>blueprint</code></td>
                                <td>Add or configure search blueprints for Liferay Search Experiences.</td>
                            </tr>
                            <tr>
                                <td rowspan="5"><strong>Microservices & Backend</strong></td>
                                <td>Object Action</td>
                                <td><code>objectAction</code></td>
                                <td>Add custom logic or behavior to Liferay Object triggers.</td>
                            </tr>
                            <tr>
                                <td>Object Validation</td>
                                <td><code>objectValidation</code></td>
                                <td>Apply custom validation rules to object fields before saving.</td>
                            </tr>
                            <tr>
                                <td>Object Entry Manager</td>
                                <td><code>objectEntryManager</code></td>
                                <td>Customize how entries are managed through APIs.</td>
                            </tr>
                            <tr>
                                <td>Workflow Action</td>
                                <td><code>workflowAction</code></td>
                                <td>Define custom actions in workflow processes.</td>
                            </tr>
                            <tr>
                                <td>Notification Type</td>
                                <td><code>notificationType</code></td>
                                <td>Create new notification channels like Slack, Webhooks, or SMS.</td>
                            </tr>
                            <tr>
                                <td rowspan="2"><strong>Batch & Initialization</strong></td>
                                <td>Batch Import/Export</td>
                                <td><code>batch</code></td>
                                <td>Import/export large datasets using CSV or JSON.</td>
                            </tr>
                            <tr>
                                <td>Site Initializer</td>
                                <td><code>siteInitializer</code></td>
                                <td>Preconfigure and deploy a full site (layouts, content, roles) automatically.</td>
                            </tr>
                        </tbody>
                    </table><br />
                    <p>For more information related to types you can also visit <a target='_blank' rel='noopener noreferrer' style={{color: 'rgb(60, 41, 136)'}} href="https://learn.liferay.com/"><strong>here.</strong></a></p>
                </section>
                <section>
                    <h2>Why Use Client Extensions Over OSGi?</h2>
                    <p>OSGi modules are powerful, but tightly coupled to Liferay’s internal implementation. CXs, on the other hand, are</p>
                    <ul>
                        <li>Easier to maintain across upgrades</li>
                        <li>Faster to deploy and test</li>
                        <li>Friendlier to front-end developers with less Liferay-specific knowledge</li>
                    </ul>
                    <p>In short, they let you focus on experience delivery rather than infrastructure concerns.</p>
                </section>
                <section>
                    <h2>Example: A Simple Custom Element CE</h2>
                    <pre><code>{`{
  "name": "my-react-widget",
  "type": "customElement",
  "url": "https://yourcdn.com/my-widget/index.js",
  "htmlElementName": "my-react-widget"
}`}
                        </code></pre>
                        <p>This would allow you to drop a <code>{`<my-react-widget></my-react-widget>`}</code> tag anywhere in a Liferay page, pulling in your external React widget.</p>
                </section>
                <section>
                    <h2>How to Deploy Client Extensions</h2>
                    <ul>
                        <li><strong>1. Create your CE config </strong>(JSON or YAML).</li>
                        <li><strong>2. Package and deploy </strong>using Liferay’s liferay-client-extension CLI or manually via the control panel</li>
                        <li><strong>3. Test and iterate </strong>without the need to restart the server.</li>
                    </ul>
                    <p>Client Extensions work seamlessly with both Liferay’s traditional UI and modern headless experiences.</p>
                </section>
                <section>
                    <h2>The Future of Liferay Customization</h2>
                    <p>Liferay Client Extensions are not just a “nice to have”—they’re the future of customizing Liferay DXP in a cloud-native, upgrade-friendly, and developer-centric way. Whether you’re building simple design tweaks or integrating full micro frontends, CEs offer a robust and scalable path forward.</p>
                </section>
                <footer>
                    <p><strong>Conclusion:</strong></p>
                    <p>
                        As Liferay evolves, so must our approach to customization. Embracing Client Extensions means unlocking a more flexible, modular, and forward-thinking way to build exceptional digital experiences.
                    </p>
                    <p>
                        If you're starting a new Liferay project or looking to modernize your existing portal, it’s time to explore what Client Extensions can do for you.
                    </p>
                    <p>Happy coding! &#128521;</p>
                </footer>
            </article>
        </div>
    );
};
